<template>
    <template-content-view :properties="true">
        <template #left>
            <folder-navigation v-if="folder != undefined" :folders="path" @navigate="navigate"
                :onCreateFolder="createFolder" :onUploadFile="uploadFile" :onUploadFolder="uploadFolder" />
            <div v-else-if="trash != undefined" class="caption">{{ $tr("Корзина") }}</div>
        </template>
        <template #right>
            <tool-button-context :actions="actions" v-if="xs"></tool-button-context>
            <tool-button v-else v-for="action in actions" :key="action.title" :title="action.title" :icon="action.icon"
                :onclick="action.onclick"></tool-button>
        </template>
        <template #content>
            <drop-zone @drop="drop">
                <table-view :controller="controller" :selection="selection" :sorter="sorter" :multiselect="true" />
            </drop-zone>
            <upload :id="id" ref="uploader" />
            <v-snackbar v-model="snackbar" :timeout="1000" color="blue-grey" rounded="pill" >
                <strong> {{ $tr('Ссылка скопирована') }}</strong>
            </v-snackbar>
        </template>
        <template #properties>
            <properties-bar :title="activeTitle" :tabs="propertiesTabs" />
        </template>
    </template-content-view>
</template>

<script>
import TemplateContentView from '@topomatic/tap/src/components/TemplateContentView.vue';
import ToolButton from '@topomatic/tap/src/components/ToolButton.vue';
import ToolButtonContext from '@topomatic/tap/src/components/ToolButtonContext.vue';
import TableView, {
    TableViewController,
} from '@topomatic/tap/src/components/TableView.vue';
import TableActionValue from '@topomatic/tap/src/components/TableActionValue.vue';
import TableSimpleValue from '@topomatic/tap/src/components/TableSimpleValue.vue';
import moment from 'moment';
import Utils from '@topomatic/tap/src/utils';
import { reactive } from 'vue';
import Dialog, { MessageBox } from '@topomatic/tap/src/dialog';
import DialogInput from '@topomatic/tap/src/components/DialogInput.vue';
import DropZone from '@topomatic/tap/src/components/DropZone.vue';
import AppConsts from '../consts';
import FileHistory from './FileHistory.vue';
import FileProperties from './FileProperties.vue';
import PropertiesBar from './PropertiesBar.vue';
import TableUserLockValue from './TableUserLockValue.vue';
import FolderNavigation from './FolderNavigation.vue';
import Upload from './UploadView.vue';
import Versions from './VersionsView.vue';
import ShareDialog from './ShareDialog.vue';

import ProgressView from './ProgressView.vue';
import { fileExtensions } from '../extensions';

const cachedFiles = {};
const states = reactive({
    loading: undefined,
    message: undefined,
    error: undefined,
});

class FolderViewController extends TableViewController {
    constructor(owner, pfiles) {
        super();
        this.files = pfiles;
        this.owner = owner;
    }

    get error() {
        return states.error;
    }

    get message() {
        return states.message;
    }

    get loading() {
        return states.loading;
    }

    headers() {
        const result = [
            this.owner.$tr('Название'),
            this.owner.$tr('Изменил'),
            this.owner.$tr('Последнее изменение'),
            this.owner.$tr('Размер файла'),
        ];
        if (this.owner.trash != undefined) {
            result[1] = this.owner.$tr('Удалил');
            result[2] = this.owner.$tr('Удалён');
        }
        if (this.owner.xs) {
            return result.slice(0, 2);
        }
        return result;
    }

    length() {
        const pfiles = this.files;
        return pfiles != undefined ? pfiles.length : 0;
    }

    id(row) {
        const pfiles = this.files;
        return pfiles[row].id;
    }

    component(row, column) {
        switch (column) {
        case 0:
            return TableActionValue;
        case 1:
            return TableUserLockValue;
        default:
            return TableSimpleValue;
        }
    }

    value(row, column) {
        const pfiles = this.files;
        const file = pfiles[row];
        switch (column) {
        case 0:
        {
            const folder = file.type == AppConsts.FOLDER_TYPE;
            const index = file.name.lastIndexOf('.');
            const ext = index > 0 ? file.name.substring(index + 1).toLowerCase() : '';
            const addOnclick = folder || fileExtensions.has(ext);
            return {
                owner: this.owner,
                file,
                onclick: addOnclick ? this.openfile : undefined,
                icon: folder ? 'folder' : 'file',
                status: file.share ? 'mdi-link-variant' : '',
                statusClick: this.statusClick,
                color: folder ? 'rgb(var(--v-theme-primary))' : '',
                value: file.name,
                ext,
            };
        }
        case 1:
            return {
                value: this.owner.$api.get_user(this.modifier(file)),
                lock: file.lock,
            };
        case 2:
            return Utils.dateTimeToString(this.modified(file));
        case 3:
            return Utils.sizeToString(this.size(file));
        default:
            return '0';
        }
    }

    modified(file) {
        if ((this.owner.trash != undefined) && (file.trashed)) {
            return file.trashed;
        }
        const rev = this.revision(file);
        let date;
        if (rev != undefined) {
            date = rev.created;
        } else if (file.modified != undefined) {
            date = file.modified;
        } else {
            date = file.created;
        }
        return date;
    }

    modifier(file) {
        if ((this.owner.trash != undefined) && (file.trasher)) {
            return file.trasher;
        }
        const rev = this.revision(file);
        if (rev != undefined) {
            return rev.creator;
        } if (file.modifier != undefined) {
            return file.modifier;
        }
        return file.creator;
    }

    size(file) {
        const rev = this.revision(file);
        if (rev != undefined) {
            return rev.length;
        }
        return undefined;
    }

    sortable(/* column */) {
        return true;
    }

    sort(column, order) {
        const pfiles = this.files;
        if (pfiles != undefined) {
            if (column == 1) {
                pfiles.sort((a, b) => {
                    const ma = this.modifier(a);
                    const mb = this.modifier(b);
                    if (ma == mb) {
                        if (a.name < b.name) {
                            return -order;
                        }
                        return order;
                    }
                    return ma < mb ? -order : order;
                });
            } else if (column == 2) {
                pfiles.sort((a, b) => {
                    const ma = moment(`${this.modified(a)}+00:00`);
                    const mb = moment(`${this.modified(b)}+00:00`);
                    if (ma == mb) {
                        if (a.name < b.name) {
                            return -order;
                        }
                        return order;
                    }
                    return ma < mb ? -order : order;
                });
            } else if (column == 3) {
                pfiles.sort((a, b) => {
                    const ma = this.size(a) || 0;
                    const mb = this.size(b) || 0;
                    if (ma == mb) {
                        if (a.name < b.name) {
                            return -order;
                        }
                        return order;
                    }
                    return ma < mb ? -order : order;
                });
            } else {
                pfiles.sort((a, b) => {
                    const afolder = a.type == AppConsts.FOLDER_TYPE;
                    const bfolder = b.type == AppConsts.FOLDER_TYPE;
                    if (afolder == bfolder) {
                        if (a.name < b.name) {
                            return -order;
                        }
                        return order;
                    } if (afolder) {
                        return -order;
                    }
                    return order;
                });
            }
        }
    }

    revision(file) {
        if ((file.revisions != undefined) && (file.revisions.length > 0)) {
            let crevision = file.revisions[0];
            for (let i = file.revisions.length - 1; i >= 0; i--) {
                if (file.revision == file.revisions[i].id) {
                    crevision = file.revisions[i];
                    break;
                }
            }
            return crevision;
        }
        return null;
    }

    async openfile(args) {
        if (fileExtensions.has(args.ext)) {
            let shareId;
            if (args.file.share) {
                shareId = args.file.share[0].id;
            } else {
                const share = await this.owner.$api.shareFile(args.file.id, undefined, 1, 60 * 60);
                shareId = share.id;
            }
            const origin = document.location.origin;
            const uri = `https://360.topomatic.ru?cloudOpenPath=${encodeURI(origin)}&shareId=${shareId}`;
            window.open(uri);
        } else {
            args.owner.navigate(args.file.id);
        }
    }

    statusClick(args) {
        if (args.file.share) {
            navigator.clipboard?.writeText(`${encodeURI(document.location.origin)}?view=share&id=${args.file.share[0].id}`);
            this.owner.snackbar = true;
        }
    }
}

let last_permissions = 0;

export default {
    components: {
        FolderNavigation,
        TableView,
        ToolButton,
        ToolButtonContext,
        PropertiesBar,
        TemplateContentView,
        DropZone,
        Upload,
    },
    name: 'FolderView',
    props: {
        root: String,
        folder: String,
        trash: String,
    },
    data() {
        return {
            data: {},
            selection: {
                selected: {},
                active: null,
            },
            sorter: reactive({
                column: 0,
                order: 1,
            }),
            mode: -1,
            updating: false,
            snackbar: null,
        };
    },
    computed: {
        display() {
            return this.$vuetify.display;
        },
        xs() {
            return this.display.xs;
        },
        id() {
            return this.folder || this.root;
        },
        controller() {
            return new FolderViewController(this, this.data.files);
        },
        selected() {
            const keys = Object.keys(this.selection.selected);
            for (let i = 0; i < keys.length; i++) {
                keys[i] = parseInt(keys[i], 10);
            }
            keys.sort((a, b) => (a < b ? -1 : 1));
            return keys;
        },
        path() {
            return this.data.path;
        },
        activeTitle() {
            const { selection } = this;
            if (selection.active != null) {
                return this.data.files[selection.active].name;
            }
            const { path } = this.data;
            if ((path != undefined) && (path.length > 0)) {
                return path[path.length - 1].name;
            }
            return '';
        },
        propertiesTabs() {
            return this.fetchPropertiesTabs();
        },
        actions() {
            const list = [];
            if (this.trash == undefined) {
                list.push({
                    title: this.$tr('Создать папку'),
                    icon: 'folder-plus',
                    onclick: this.createFolder,
                });
                list.push({
                    title: this.$tr('Загрузить файлы'),
                    icon: 'file-plus',
                    onclick: this.uploadFile,
                });
            }
            const { selected } = this;
            if (this.trash) {
                if (selected.length > 0) {
                    list.push({
                        title: this.$tr('Восстановить'),
                        icon: 'delete-restore',
                        onclick: this.restoreSelectedFile,
                    });
                } else {
                    list.push({
                        title: this.$tr('Очистить корзину'),
                        icon: 'delete-alert',
                        onclick: this.clearTrash,
                    });
                }
            }
            if (selected.length == 1) {
                list.push({
                    title: this.$tr('Переименовать'),
                    icon: 'pencil',
                    onclick: this.renameFile,
                });
            }
            if (selected.length > 0) {
                list.push({
                    title: this.$tr('Скачать'),
                    icon: 'download',
                    onclick: () => this.downloadItem(),
                });
            }
            if (selected.length == 1) {
                list.push({
                    title: this.$tr('Управление версиями'),
                    icon: 'restore',
                    onclick: this.versionFile,
                });
            }
            if (selected.length == 1) {
                const file = this.data.files[selected[0]];
                const index = file.name.lastIndexOf('.');
                const ext = index > 0 ? file.name.substring(index + 1).toLowerCase() : '';
                if (fileExtensions.has(ext)) {
                    list.push({
                        title: this.$tr('Поделиться доступом'),
                        icon: 'link',
                        onclick: this.shareItem,
                    });
                    if (file.share) {
                        list.push({
                            title: this.$tr('Удалить доступ к файлу по ссылке'),
                            icon: 'link-off',
                            onclick: this.deleteShareLink,
                        });
                    }
                }
            }
            if (selected.length >= 1 && this.data.files[this.selected[0]].type == AppConsts.FOLDER_TYPE) {
                list.push({
                    title: this.$tr('Скачать шаблон'),
                    icon: 'account-arrow-down',
                    onclick: () => this.downloadItem(true),
                });
            }
            if (!this.trash) {
                list.push({
                    title: this.$tr('Загрузить шаблон'),
                    icon: 'account-arrow-up',
                    onclick: this.uploadTemplate,
                });
            }
            if (selected.length > 0) {
                list.push({
                    title: this.$tr('Удалить'),
                    icon: 'delete-forever',
                    onclick: this.deleteFile,
                });
            }
            return list;
        },
    },
    methods: {
        subscribe() {
            const { trash } = this;
            if (trash != undefined) {
                const me = this.$api.me.user;
                this.$api.getDrives().then((drives) => {
                    const folders = [];
                    for (let i = 0; i < drives.length; i++) {
                        const drive = drives[i];
                        if (trash == 'my') {
                            if (drive.user == me.id) {
                                folders.push(drive.id);
                            }
                        } else if (trash == drive.company) {
                            folders.push(drive.id);
                        }
                    }
                    this.$api.wsSend({
                        event: 'watch',
                        depth: -1,
                        folders,
                    });
                });
            } else {
                this.$api.wsSend({
                    event: 'watch',
                    depth: 1,
                    folders: [
                        this.id,
                    ],
                });
            }
        },
        fetchPropertiesTabs() {
            const { data } = this;
            if (data == undefined) {
                return [];
            }
            let file;
            const cpath = data.path;
            const path = [];
            if (cpath != undefined) {
                for (let i = 0; i < cpath.length; i++) {
                    path.push(cpath[i]);
                }
                if (this.selection.active == undefined) {
                    if (path.length == 0) {
                        return [];
                    }
                    file = path[path.length - 1];
                } else {
                    file = data.files[this.selection.active];
                    path.push(file);
                }
            } else if (this.selection.active != undefined) {
                file = data.files[this.selection.active];
            }
            const scope = {
                file,
                path: reactive(path),
                permissions: reactive({
                    value: last_permissions,
                }),
            };
            if (file != undefined) {
                this.$api.getFile(scope.file.id, true, true, true)
                    .then((pdata) => {
                        const keys = Object.keys(pdata.file);
                        for (let i = 0; i < keys.length; i++) {
                            const key = keys[i];
                            scope.file[key] = pdata.file[key];
                        }
                        scope.permissions.value = pdata.permissions;
                        last_permissions = pdata.permissions;
                        pdata.path.sort((a, b) => (a.depth < b.depth ? -1 : 1));
                        scope.path.splice(0, scope.path.length);
                        for (let i = 0; i < pdata.path.length; i++) {
                            scope.path.push(pdata.path[i]);
                        }
                        scope.path.push(pdata.file);
                    });
                return [{
                    title: this.$tr('Свойства'),
                    component: FileProperties,
                    data: scope,
                },
                {
                    title: this.$tr('История'),
                    component: FileHistory,
                    data: scope,
                },
                ];
            }
            return [];
        },
        fetchData() {
            const { trash } = this;
            if (trash != undefined) {
                this.$api.getTrash(trash == AppConsts.APP_GROUP_MY ? undefined : trash)
                    .then((data) => {
                        if (this.trash == trash) {
                            this.data = data;
                        }
                    });
            } else {
                const { id } = this;
                let data = cachedFiles[id];
                if (data == undefined) {
                    data = {
                        files: [],
                        path: [],
                    };
                }
                this.data = data;
                this.$api.getFiles(id, 1, true, false, true)
                    .then((cres) => {
                        cres.path.sort((a, b) => (a.depth < b.depth ? -1 : 1));
                        const file = {
                            id,
                            path: cres.path,
                            files: [],
                        };
                        const folders = {};
                        const items = {};
                        for (let i = 0; i < cres.files.length; i++) {
                            const f = cres.files[i];
                            if (f.type == AppConsts.FOLDER_TYPE) {
                                items[f.id] = f;
                            }
                            if (f.parent == id) {
                                file.files.push(f);
                            } else {
                                let folder = folders[f.parent];
                                if (folder == undefined) {
                                    folder = {
                                        id: f.parent,
                                        path: [],
                                        files: [],
                                    };
                                    folders[f.parent] = folder;
                                }
                                folder.files.push(f);
                            }
                        }
                        if (this.id == id) {
                            this.data = file;
                        }
                        cachedFiles[id] = file;
                        const keys = Object.keys(folders);
                        for (let i = 0; i < keys.length; i++) {
                            const folder = folders[keys[i]];
                            for (let j = 0; j < file.path.length; j++) {
                                folder.path.push(file.path[j]);
                            }
                            const item = items[folder.id];
                            if (item != undefined) {
                                folder.path.push(item);
                            }
                            cachedFiles[folder.id] = folder;
                        }
                    });
            }
        },
        navigate(id) {
            const { payload } = this.$router;
            const keys = Object.keys(payload);
            const route = {};
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                route[key] = payload[key];
            }
            if (route.drive != id) {
                route.folder = id;
            } else {
                delete route.folder;
            }
            this.$router.push(route);
        },
        fileExists(name) {
            const pfiles = this.data.files;
            name = name.toLowerCase();
            for (let i = 0; i < pfiles.length; i++) {
                const file = pfiles[i];
                if ((file.name.toLowerCase() == name) && (file.type == AppConsts.FOLDER_TYPE)) {
                    return true;
                }
            }
            return false;
        },
        createFolder() {
            const payload = {
                title: this.$tr('Новая папка'),
                ok: this.$tr('Создать'),
                placeholder: this.$tr('Название папки'),
                value: this.$tr('Новая папка'),
                focus: true,
                validate: (value) => (value || '').trim().length > 0,
            };
            Dialog(DialogInput, payload)
                .then(() => {
                    payload.value = payload.value.trim();
                    if (!this.fileExists(payload.value)) {
                        this.$api.addFolder(payload.value, this.id)
                            .then();
                    }
                });
        },
        shareItem() {
            const me = this.$api.me.user;
            const file = this.data.files[this.selected[0]];
            const fType = file.type === AppConsts.FOLDER_TYPE ? 'папке' : 'файлу';
            const payload = {
                title: `Поделиться доступом к ${fType}`,
                api: this.$api,
                ok: this.$tr('Ок'),
                file,
                me,
                cancel: '',
            };
            Dialog(ShareDialog, payload).then(() => {
            }).catch(() => {});
        },
        deleteShareLink() {
            const file = this.data.files[this.selected[0]];
            const msg = this.$tr('Вы действительно хотите удалить доступ к файлу?');
            const payload = {
                ok: this.$tr('Удалить'),
                destructive: true,
                finished: false,
            };
            MessageBox(this.$tr('Удалить'), msg, payload).then(() => {
                this.$api.removeShare(file.id).catch(() => {
                    payload.finished = true;
                });
                delete file.share;
            }).catch(() => {
                payload.finished = true;
            });
        },
        renameFile() {
            const file = this.data.files[this.selected[0]];
            const payload = {
                title: this.$tr('Переименовать'),
                ok: this.$tr('Переименовать'),
                placeholder: this.$tr('Название'),
                value: file.name,
                focus: true,
                validate: (value) => (value || '').trim().length > 0,
            };
            Dialog(DialogInput, payload)
                .then(() => {
                    payload.value = payload.value.trim();
                    if (!this.fileExists(payload.value)) {
                        this.$api.renameFile(file.id, payload.value)
                            .then((drive) => {
                                file.name = drive.file.name;
                            });
                    }
                });
        },
        async deleteFiles(files) {
            const payload = reactive({
                ok: this.$tr('Отмена'),
                cancel: '',
                error: false,
                progress: 0,
                content: '',
                finished: false,
                title: this.$tr('Удаление'),
            });
            setImmediate(async () => {
                const workers = {};
                for (let i = 0; i < files.length; i += 1) {
                    const file = files[i];
                    if (Object.keys(workers).length > 5) {
                        try {
                            await Promise.race(Object.values(workers));
                        } catch {
                            // pass
                        }
                    }
                    if (payload.finished) {
                        break;
                    }
                    payload.content = file.name;
                    payload.progress = (i * 100) / files.length;
                    workers[file.id] = this.$api.removeFile(file.id).finally(() => {
                        delete workers[file.id];
                    });
                }
                payload.progress = 100;
                try {
                    await Promise.all(Object.values(workers));
                } catch {
                    // pass
                }
                payload.finished = true;
            });
            try {
                await Dialog(ProgressView, payload);
            } finally {
                payload.finished = true;
            }
        },
        deleteFile() {
            const indexes = this.selected;
            const pfiles = this.data.files;
            let msg;
            if (this.trash != undefined) {
                msg = this.$tr('Выбранные объекты будут удалены без возможности восстановления. Внимание! Отменить это действие невозможно.');
            } else {
                msg = this.$tr('Вы действительно хотите удалить выбранные элементы?');
            }
            const payload = {
                ok: this.$tr('Удалить'),
                destructive: true,
            };
            MessageBox(this.$tr('Удалить'), msg, payload)
                .then(() => {
                    this.selection.selected = {};
                    this.selection.active = null;
                    const files = [];
                    for (let i = 0; i < indexes.length; i++) {
                        const file = pfiles[indexes[i]];
                        files.push(file);
                    }
                    this.deleteFiles(files);
                });
        },
        clearTrash() {
            if (this.trash != undefined) {
                const msg = this.$tr('Выбранные объекты будут удалены без возможности восстановления. Внимание! Отменить это действие невозможно.');
                const payload = {
                    ok: this.$tr('Удалить'),
                    destructive: true,
                };
                MessageBox(this.$tr('Удалить'), msg, payload)
                    .then(() => {
                        this.selection.selected = {};
                        this.selection.active = null;
                        const files = [];
                        files.push(...this.data.files);
                        this.deleteFiles(files);
                    });
            }
        },
        restoreFile(file) {
            return new Promise((resolve) => {
                this.$api.getFiles(file.parent, 0, false)
                    .then((data) => {
                        const pfiles = data.files;
                        let { name } = file;
                        let exists = false;
                        for (let i = 0; i < pfiles.length; i++) {
                            const efile = pfiles[i];
                            if (efile.name.toLowerCase() == name.toLowerCase()) {
                                exists = true;
                                break;
                            }
                        }
                        if (exists) {
                            const index = 1;
                            let path = name;
                            let ext = '';
                            const s = name.lastIndexOf('.');
                            if (s > 0) {
                                path = name.substring(0, s);
                                ext = name.substring(s);
                            }
                            while (exists) {
                                exists = false;
                                name = `${path} (${index})${ext}`;
                                for (let i = 0; i < pfiles.length; i++) {
                                    const efile = pfiles[i];
                                    if (efile.name.toLowerCase() == name.toLowerCase()) {
                                        exists = true;
                                        break;
                                    }
                                }
                            }
                            this.$api.renameFile(file.id, name)
                                .then(() => {
                                    this.$api.restoreFile(file.id).finally(resolve);
                                })
                                .catch(resolve);
                        } else {
                            this.$api.restoreFile(file.id).finally(resolve);
                        }
                    })
                    .catch(resolve);
            });
        },
        restoreSelectedFile() {
            const indexes = this.selected;
            const pfiles = this.data.files;
            const items = [];
            for (let i = 0; i < indexes.length; i++) {
                const file = pfiles[indexes[i]];
                items.push(file);
            }
            if (items.length > 0) {
                const resolved = function () {
                    if (items.length > 0) {
                        const file = items.pop();
                        this.restoreFile(file).finally(resolved);
                    } else {
                        for (let i = indexes.length - 1; i >= 0; i--) {
                            pfiles.splice(indexes[i], 1);
                        }
                        this.selection.selected = {};
                        this.selection.active = undefined;
                    }
                }.bind(this);
                resolved();
            }
        },
        uploadFile() {
            this.$refs.uploader.uploadFile();
        },
        uploadFolder() {
            this.$refs.uploader.uploadFolder();
        },
        drop(pfiles) {
            this.$refs.uploader.uploadFiles(pfiles);
        },
        downloadItem(getMetadata = false) {
            const indexes = this.selected;
            const pfiles = this.data.files;
            const items = [];
            for (let i = 0; i < indexes.length; i++) {
                const file = pfiles[indexes[i]];
                items.push(file);
            }
            this.$refs.uploader.downloadFiles(items, getMetadata);
        },
        uploadTemplate() {
            this.$refs.uploader.uploadFile();
        },
        versionFile() {
            const { id } = this.data.files[this.selected[0]];
            this.$api.getFile(id, true, false)
                .then((data) => {
                    data.file.revisions.sort((a, b) => (a.n < b.n ? 1 : -1));
                    const download = function (file, revision) {
                        this.$refs.uploader.downloadRevision(file.id, revision.id);
                    }.bind(this);
                    const payload = {
                        title: this.$tr('Управление версиями'),
                        cancel: '',
                        file: data.file,
                        download,
                        api: this.$api,
                    };
                    Dialog(Versions, payload);
                });
        },
        update() {
            if (!this.updating) {
                this.updating = true;
                setTimeout(() => {
                    this.updating = false;
                    this.fetchData();
                }, 500);
            }
        },
    },
    created() {
        this.fetchData();
        this.subscribe();
    },
    watch: {
        id() {
            this.selection.active = null;
            this.selection.selected = [];
            this.fetchData();
            this.subscribe();
        },
        trash() {
            this.selection.active = null;
            this.selection.selected = [];
            this.fetchData();
            this.subscribe();
        },
    },
    mounted() {
        this.watch = (msg) => {
            if (this.data != undefined) {
                if (this.trash) {
                    if ((msg.action == 'update') || (msg.action == 'lock') || (msg.action == 'unlock')) {
                        const index = this.data.files.findIndex((a) => a.id == msg.file.id);
                        if (index >= 0) {
                            this.data.files[index] = msg.file;
                        }
                    } else if (msg.action == 'trash') {
                        this.update();
                        this.data.files.push(msg.file);
                    } else if ((msg.action == 'restore') || (msg.action == 'delete')) {
                        this.update();
                        const index = this.data.files.findIndex((a) => a.id == msg.file.id);
                        if (index >= 0) {
                            this.data.files.splice(index, 1);
                        }
                    }
                } else if (msg.file?.parent == this.id) {
                    this.update();
                    if (msg.action == 'create') {
                        this.data.files.push(msg.file);
                    } else if ((msg.action == 'update') || (msg.action == 'lock') || (msg.action == 'unlock')) {
                        const index = this.data.files.findIndex((a) => a.id == msg.file.id);
                        if (index >= 0) {
                            this.data.files[index] = msg.file;
                        }
                    } else if (msg.action == 'trash') {
                        const index = this.data.files.findIndex((a) => a.id == msg.file.id);
                        if (index >= 0) {
                            this.data.files.splice(index, 1);
                        }
                    } else if (msg.action == 'restore') {
                        this.data.files.push(msg.file);
                    }
                } else if (msg.file?.id == this.id) {
                    this.update();
                }
            }
        };
        this.$api.wsRegister('watch', this.watch);
        this.permissions = (msg) => {
            if (this.data != undefined) {
                if (!this.trash) {
                    for (let i = 0; i < msg.path.length; i++) {
                        const path = msg.path[i];
                        if (path.id == this.id) {
                            this.update();
                            break;
                        }
                    }
                }
            }
        };
        this.$api.wsRegister('permissions', this.permissions);
    },
    unmounted() {
        this.$api.wsUnregister('watch', this.watch);
        this.$api.wsUnregister('permissions', this.permissions);
    },
};
</script>

<style scoped>
.caption {
    font-size: 1.75rem;
    align-self: center;
    padding-left: 16px;
}
</style>
